/**
 * Thin Amplify Auth wrapper (v6 functional APIs). Used by AuthStore and auth screens only.
 */
import {
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  signIn,
  signOut as amplifySignOut,
  signUp,
} from "aws-amplify/auth";

/** Custom challenge (passwordless OTP) — pass on every signIn for this app. */
export const customAuthSignInOptions = { authFlowType: "CUSTOM_WITHOUT_SRP" };

/**
 * Returns a v5-shaped user object so AuthStore can stay stable: username, attributes, signInUserSession.groups.
 * @param {{ forceRefresh?: boolean }} [opts]
 */
export async function getCurrentUserInfoV5Shaped(opts = {}) {
  const user = await getCurrentUser();
  const session = await fetchAuthSession({ forceRefresh: opts.forceRefresh === true });
  const attributes = await fetchUserAttributes();
  const groups = session.tokens?.accessToken?.payload?.["cognito:groups"] ?? [];
  return {
    username: user.username,
    attributes,
    signInUserSession: {
      accessToken: {
        payload: { "cognito:groups": groups },
      },
      idToken: {
        payload: {
          email: attributes.email,
        },
      },
    },
  };
}

export async function startCustomAuthSignIn(username) {
  const doSignIn = () =>
    signIn({
      username,
      options: customAuthSignInOptions,
    });

  try {
    return await doSignIn();
  } catch (e) {
    const code = e?.name ?? e?.code;
    if (code === "UserAlreadyAuthenticatedException") {
      await amplifySignOut();
      return await doSignIn();
    }
    throw e;
  }
}

export async function submitCustomAuthChallenge(challengeResponse) {
  return confirmSignIn({ challengeResponse });
}

export {
  amplifySignOut as signOut,
  confirmSignUp,
  resendSignUpCode,
  signUp,
  signIn,
  confirmSignIn,
  getCurrentUser,
};

export async function ensureAuthenticatedUser() {
  await getCurrentUser();
}
